import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { ShoppingCart, Users, Building, BarChart3, Edit3, CheckSquare, Layout, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'shopping-cart': ShoppingCart,
  'users': Users,
  'building': Building,
  'bar-chart': BarChart3,
  'edit': Edit3,
  'check-square': CheckSquare,
  'layout': Layout,
};

export const TemplatesSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    if (!user) {
      navigate('/register');
      return;
    }
    navigate('/dashboard', { state: { prompt: template.prompt, templateId: template.id } });
  };

  if (loading) {
    return (
      <section id="templates" className="py-20 px-4">
        <div className="max-w-6xl mx-auto flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
        </div>
      </section>
    );
  }

  return (
    <section id="templates" className="py-20 px-4 bg-gradient-to-b from-transparent to-white/[0.02]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Templates de Proyectos
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Empieza con una plantilla profesional y personalízala según tus necesidades
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template, index) => {
            const Icon = iconMap[template.icon] || Layout;
            return (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
                onMouseEnter={() => setSelectedTemplate(template.id)}
                onMouseLeave={() => setSelectedTemplate(null)}
              >
                <div className="glass rounded-2xl p-6 h-full border border-white/10 hover:border-white/20 transition-all duration-300 group-hover:transform group-hover:scale-[1.02]">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2">{template.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{template.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {template.features.slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
                        {feature}
                      </div>
                    ))}
                    {template.features.length > 3 && (
                      <p className="text-xs text-gray-600">+{template.features.length - 3} más...</p>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleUseTemplate(template)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 hover:border-[#FF6B00]/50 transition-all group-hover:border-[#FF6B00]/30"
                    data-testid={`use-template-${template.id}`}
                  >
                    <span className="text-sm font-medium">Usar Template</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
